import { handleResponse, handleBlobResponse } from "utils/utils";
import { authService } from "services";
import i18n from "i18n";

/** Valores canónicos en UI (español). */
export const shiftType = {
	work: "Trabajo",
	automatic: "Automático",
	absence: "Ausencia",
	vacation: "Vacaciones",
	rest: "Descanso",
};

let shiftTypesCache = null;
let shiftTypesFetchPromise = null;

i18n.on("languageChanged", () => {
	shiftTypesCache = null;
	shiftTypesFetchPromise = null;
});

function extractShiftTypesList(types) {
	if (Array.isArray(types)) return types;
	if (Array.isArray(types?.data)) return types.data;
	return [];
}

/** Cachea los tipos devueltos por GET /shifts/types (fuente de verdad del API). */
export function setShiftTypesCache(types) {
	shiftTypesCache = extractShiftTypesList(types);
}

function getCanonicalToApiNameMap() {
	const map = {};
	for (const item of shiftTypesCache || []) {
		if (!item?.name) continue;
		map[normalizeShiftType(item.name)] = item.name;
	}
	return map;
}

async function ensureShiftTypesCache() {
	if (shiftTypesCache?.length) return shiftTypesCache;

	if (!shiftTypesFetchPromise) {
		shiftTypesFetchPromise = getTypes()
			.then((response) => {
				setShiftTypesCache(response);
				return shiftTypesCache;
			})
			.finally(() => {
				shiftTypesFetchPromise = null;
			});
	}

	return shiftTypesFetchPromise;
}

/** Valor del filtro «Todos» en el listado de turnos. */
export const shiftTypeFilterAll = "";

const SHIFT_TYPE_ALIASES = {
	Trabajo: shiftType.work,
	Work: shiftType.work,
	Treball: shiftType.work,
	Attendance: shiftType.work,
	Automático: shiftType.automatic,
	Automatic: shiftType.automatic,
	"Automàtic": shiftType.automatic,
	Disponibilidad: shiftType.automatic,
	Availability: shiftType.automatic,
	Ausencia: shiftType.absence,
	Absence: shiftType.absence,
	Absent: shiftType.absence,
	"Absència": shiftType.absence,
	Vacaciones: shiftType.vacation,
	Vacation: shiftType.vacation,
	Vacances: shiftType.vacation,
	Descanso: shiftType.rest,
	Rest: shiftType.rest,
	Descans: shiftType.rest,
};

/** Normaliza alias/legacy del API al tipo canónico en español (UI). */
export function normalizeShiftType(type) {
	if (!type) return "";
	return SHIFT_TYPE_ALIASES[type] || type;
}

/** Convierte el tipo canónico de UI al `name` que devuelve GET /shifts/types. */
export function toApiShiftType(type) {
	if (!type) return type;

	const apiNames = getCanonicalToApiNameMap();
	const normalized = normalizeShiftType(type);

	if (apiNames[normalized]) return apiNames[normalized];

	if (shiftTypesCache?.some((item) => item.name === type)) return type;

	return normalized;
}

/** Convierte valores de `shift_type` en query (simple o separados por coma). */
export function toApiShiftTypeQuery(shiftTypeParam) {
	if (shiftTypeParam === undefined || shiftTypeParam === "") return shiftTypeParam;
	return shiftTypeParam
		.split(",")
		.map((value) => toApiShiftType(value.trim()))
		.join(",");
}

export function normalizeShiftFromApi(shift) {
	if (!shift || typeof shift !== "object") return shift;
	return {
		...shift,
		type: normalizeShiftType(shift.type),
	};
}

function normalizeShiftsListResponse(response) {
	if (!response?.ok) return response;

	if (Array.isArray(response.data)) {
		return {
			...response,
			data: response.data.map(normalizeShiftFromApi),
		};
	}

	if (response.type !== undefined) {
		return normalizeShiftFromApi(response);
	}

	return response;
}

export function matchesShiftTypeFilter(shift, filterTypeId) {
	if (!filterTypeId) return true;
	return normalizeShiftType(shift.type) === filterTypeId;
}

function resolveShiftTypeQuery(prop) {
	const resolve = (canonical) => toApiShiftType(canonical);
	if (prop === "attendance") {
		return [shiftType.work, shiftType.automatic].map(resolve).join(",");
	}
	if (prop === "absence") {
		return [shiftType.absence, shiftType.vacation].map(resolve).join(",");
	}
	return undefined;
}

export const shiftTypeQuery = new Proxy(
	{},
	{
		get(_, prop) {
			return resolveShiftTypeQuery(prop);
		},
	}
);

export const shiftsService = {
	getAll,
	getDefault,
	getSelectable,
	getByAttendance,
	getById,
	add,
	update,
	remove,
	getTypes,
	downloadImportTemplate,
	prepareImport,
	runImport,
};

// gets all shifts
async function getAll(options = {}) {
	await ensureShiftTypesCache();

	const { q, shift_type, group_id, contract_only, page_size, sort, page } =
		options;

	const requestOptions = {
		method: "GET",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Accept-Language": i18n.language || "es",
		},
	};
	let query = `?sort=${sort || "+name"}&page_size=${page_size ?? 10000}`;

	let { active } = options;

	// DEFAULT solo si viene undefined (no si viene null)
	if (active === undefined) active = true;

	// solo añadimos query si active es true/false
	if (active === true || active === false) query += "&active=" + active;

	if (q !== undefined) query += "&q=" + q;

	if (shift_type !== undefined && shift_type !== "")
		query += "&shift_type=" + toApiShiftTypeQuery(shift_type);

	if (group_id !== undefined) query += "&group_id=" + group_id;

	if (contract_only !== undefined) query += "&contract_only=" + contract_only;

	if (page !== undefined) query += "&page=" + page;

	return fetch(`${global.BASE_API}shifts${query}`, requestOptions)
		.then(handleResponse)
		.then(normalizeShiftsListResponse);
}

// get default shift
function getDefault() {
	const requestOptions = {
		method: "GET",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Accept-Language": i18n.language || "es",
		},
	};
	return fetch(`${global.BASE_API}shifts/default`, requestOptions)
		.then(handleResponse)
		.then(normalizeShiftsListResponse);
}

// gets selectable shifts
async function getSelectable(options = {}) {
	await ensureShiftTypesCache();

	const requestOptions = {
		method: "GET",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Accept-Language": i18n.language || "es",
		},
	};

	let query = "?sort=+name&variant=select&active=true&page_size=10000";

	if (options) {
		const { q, shift_type, group_id } = options;

		if (q !== undefined) query += "&q=" + q;

		if (shift_type !== undefined)
			query += "&shift_type=" + toApiShiftTypeQuery(shift_type);

		if (group_id !== undefined) query += "&group_id=" + group_id;
	}

	return fetch(`${global.BASE_API}shifts${query}`, requestOptions)
		.then(handleResponse)
		.then(normalizeShiftsListResponse);
}

async function getByAttendance(isAttendance) {
	await ensureShiftTypesCache();

	const requestOptions = {
		method: "GET",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Accept-Language": i18n.language || "es",
		},
	};
	const shift_type = isAttendance
		? shiftTypeQuery.attendance
		: shiftTypeQuery.absence;

	let query = "?sort=+name&variant=select&active=true&shift_type=" + shift_type;

	return fetch(`${global.BASE_API}shifts${query}`, requestOptions)
		.then(handleResponse)
		.then(normalizeShiftsListResponse);
}

// get one shift by id
function getById(id) {
	const requestOptions = {
		method: "GET",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Accept-Language": i18n.language || "es",
		},
	};

	return fetch(`${global.BASE_API}shifts/${id}`, requestOptions)
		.then(handleResponse)
		.then(normalizeShiftsListResponse);
}

function prepareShiftForApi(shift) {
	return {
		...shift,
		type: toApiShiftType(shift.type),
	};
}

// add new shift
async function add(shift) {
	await ensureShiftTypesCache();

	const requestOptions = {
		method: "POST",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Accept-Language": i18n.language || "es",
			"Content-Type": "application/json",
		},
		body: JSON.stringify(prepareShiftForApi(shift)),
	};

	return fetch(`${global.BASE_API}shifts/`, requestOptions)
		.then(handleResponse)
		.then(normalizeShiftsListResponse);
}

// update shift
async function update(shift) {
	await ensureShiftTypesCache();

	const requestOptions = {
		method: "PUT",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Accept-Language": i18n.language || "es",
			"Content-Type": "application/json",
		},
		body: JSON.stringify(prepareShiftForApi(shift)),
	};

	return fetch(`${global.BASE_API}shifts/${shift.id}`, requestOptions)
		.then(handleResponse)
		.then(normalizeShiftsListResponse);
}

// remove shift
function remove(id) {
	const requestOptions = {
		method: "DELETE",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Accept-Language": i18n.language || "es",
		},
	};

	return fetch(`${global.BASE_API}shifts/${id}`, requestOptions).then(
		handleResponse
	);
}

// get shift types
function getTypes() {
	const requestOptions = {
		method: "GET",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Accept-Language": i18n.language || "es",
		},
	};

	return fetch(`${global.BASE_API}shifts/types`, requestOptions)
		.then(handleResponse)
		.then((response) => {
			setShiftTypesCache(response);
			return response;
		});
}

/**********************************************************************
 ****** IMPORT SHIFTS
 ***********************************************************************/

function downloadImportTemplate() {
	const requestOptions = {
		method: "GET",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Accept-Language": i18n.language || "es",
			"Content-Type": "application/json",
		},
	};

	return fetch(
		`${global.BASE_API}shifts/imports/template`,
		requestOptions
	).then(handleBlobResponse);
}

function prepareImport(formData) {
	const requestOptions = {
		method: "POST",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Accept-Language": i18n.language || "es",
		},
		body: formData,
	};

	return fetch(`${global.BASE_API}shifts/imports`, requestOptions).then(
		handleResponse
	);
}

function runImport(token) {
	const requestOptions = {
		method: "POST",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Accept-Language": i18n.language || "es",
		},
	};

	return fetch(
		`${global.BASE_API}shifts/imports/${token}`,
		requestOptions
	).then(handleResponse);
}
